/**
 * @fileoverview Servizio per periodi (periods) e logica periodo attivo / chiusura.
 */
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  COLLECTIONS,
  PERIODS_FIELDS,
  RATINGS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  RATING_MODES,
  RATING_PRESETS,
  ELO_CONFIG,
  SEASON_START_OPTIONS,
  MATCHES_FIELDS,
  RATING_IMPACT,
} from '../domain/dataModel';
import { saveHallOfFame } from './hallOfFameService';
import { createRating, getRatingsByPeriod } from './ratingService';

/**
 * Recupera il periodo attivo del gruppo (startDate <= now <= endDate).
 * @param {string} groupId
 * @returns {Promise<{ id: string, ...Object } | null>}
 */
export async function getActivePeriod(groupId) {
  try {
    if (!groupId) return null;
    const periods = await getPeriodsByGroup(groupId);
    const now = Timestamp.now().toDate();
    return periods.find((p) => {
      const start = p[PERIODS_FIELDS.START_DATE]?.toDate?.();
      const end = p[PERIODS_FIELDS.END_DATE]?.toDate?.();
      if (!start || !end) return false;
      return start <= now && now <= end;
    }) || null;
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento del periodo attivo.');
  }
}

/** Rating iniziale da carry-over o base. */
function _initialRatingForMember(m, prevRatings) {
  const isGuest = m[GROUP_MEMBERS_FIELDS.IS_GUEST];
  if (isGuest) {
    const r = prevRatings.find((x) => x[RATINGS_FIELDS.MEMBER_ID] === m.id);
    return r != null ? (Number(r[RATINGS_FIELDS.RATING]) || ELO_CONFIG.RATING_BASE) : ELO_CONFIG.RATING_BASE;
  }
  const uid = m[GROUP_MEMBERS_FIELDS.USER_ID];
  if (!uid) return ELO_CONFIG.RATING_BASE;
  const r = prevRatings.find((x) => x[RATINGS_FIELDS.USER_ID] === uid);
  return r != null ? (Number(r[RATINGS_FIELDS.RATING]) || ELO_CONFIG.RATING_BASE) : ELO_CONFIG.RATING_BASE;
}

/**
 * Crea un nuovo periodo e i rating iniziali per ogni membro (userId o memberId se guest).
 * @param {string} groupId
 * @param {Object} data - { name, startDate, endDate, ratingMode, ratingPreset, seasonStartOption, previousPeriodId }
 *   seasonStartOption: 'RESET' | 'CARRY_OVER' — se CARRY_OVER, previousPeriodId deve essere presente in data
 * @param {string} createdBy - UID chi crea (non salvato in periodo nel dataModel attuale)
 * @param {Array<{ id: string, [GROUP_MEMBERS_FIELDS.USER_ID]: string|null, [GROUP_MEMBERS_FIELDS.IS_GUEST]: boolean }>} members - Membri attivi per creare i rating
 * @returns {Promise<{ periodId: string }>}
 */
export async function createPeriod(groupId, data, createdBy, members = []) {
  try {
    if (!groupId) throw new Error('Gruppo mancante.');
    const name = (data?.name ?? data?.description ?? '').trim();
    if (!name) throw new Error('Inserisci una descrizione per il periodo.');
    const start = data.startDate instanceof Date ? Timestamp.fromDate(data.startDate) : data.startDate;
    const end = data.endDate instanceof Date ? Timestamp.fromDate(data.endDate) : data.endDate;
    if (!start || !end) throw new Error('Inserisci data inizio e data fine.');

    const seasonOpt = data.seasonStartOption ?? SEASON_START_OPTIONS.RESET;
    if (seasonOpt === SEASON_START_OPTIONS.CARRY_OVER && !data.previousPeriodId) {
      throw new Error('Per portare avanti i rating serve la stagione precedente (previousPeriodId).');
    }

    let prevRatings = [];
    if (seasonOpt === SEASON_START_OPTIONS.CARRY_OVER && data.previousPeriodId) {
      prevRatings = await getRatingsByPeriod(groupId, data.previousPeriodId);
    }

    const periodRef = await addDoc(collection(db, COLLECTIONS.PERIODS), {
      [PERIODS_FIELDS.GROUP_ID]: groupId,
      [PERIODS_FIELDS.NAME]: name,
      [PERIODS_FIELDS.START_DATE]: start,
      [PERIODS_FIELDS.END_DATE]: end,
      [PERIODS_FIELDS.RATING_MODE]: data.ratingMode ?? RATING_MODES.RESULT_ONLY,
      [PERIODS_FIELDS.RATING_PRESET]: data.ratingPreset ?? RATING_PRESETS.CLASSIC,
      [PERIODS_FIELDS.CREATED_AT]: serverTimestamp(),
      [PERIODS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });

    for (const m of members) {
      const isGuest = m[GROUP_MEMBERS_FIELDS.IS_GUEST];
      const initial =
        seasonOpt === SEASON_START_OPTIONS.CARRY_OVER && data.previousPeriodId
          ? _initialRatingForMember(m, prevRatings)
          : ELO_CONFIG.RATING_BASE;
      if (isGuest) {
        await createRating(groupId, periodRef.id, m.id, null, initial);
      } else {
        const uid = m[GROUP_MEMBERS_FIELDS.USER_ID];
        if (uid) await createRating(groupId, periodRef.id, null, uid, initial);
      }
    }
    return { periodId: periodRef.id };
  } catch (err) {
    throw new Error(err?.message || 'Errore nella creazione del periodo.');
  }
}

/**
 * Chiude il periodo: salva hallOfFame con le classifiche finali e imposta endDate a now.
 * Non azzera i documenti rating.
 * @param {string} periodId
 * @param {string} groupId
 * @returns {Promise<void>}
 */
export async function closePeriod(periodId, groupId) {
  try {
    if (!periodId || !groupId) throw new Error('Periodo o gruppo mancante.');

    const ratingsSnap = await getDocs(
      query(
        collection(db, COLLECTIONS.RATINGS),
        where(RATINGS_FIELDS.GROUP_ID, '==', groupId),
        where(RATINGS_FIELDS.PERIOD_ID, '==', periodId)
      )
    );
    const ratingsList = ratingsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const sorted = ratingsList.sort(
      (a, b) => (b[RATINGS_FIELDS.RATING] ?? 0) - (a[RATINGS_FIELDS.RATING] ?? 0)
    );
    const rankings = sorted.map((r, idx) => {
      const played = r[RATINGS_FIELDS.MATCHES_PLAYED] ?? 0;
      const wins = r[RATINGS_FIELDS.WINS] ?? 0;
      const winRate = played > 0 ? Math.round((wins / played) * 100) : 0;
      return {
        position: idx + 1,
        memberId: r[RATINGS_FIELDS.MEMBER_ID] || r[RATINGS_FIELDS.USER_ID],
        finalRating: r[RATINGS_FIELDS.RATING] ?? 0,
        matchesPlayed: played,
        matchesWon: wins,
        winRate,
        gamesWon: r.gamesWon ?? 0,
      };
    });

    await saveHallOfFame(groupId, periodId, rankings);

    const closedAt = Timestamp.now();
    const periodRef = doc(db, COLLECTIONS.PERIODS, periodId);
    await updateDoc(periodRef, {
      [PERIODS_FIELDS.END_DATE]: closedAt,
      [PERIODS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
  } catch (err) {
    throw new Error(err?.message || 'Errore nella chiusura del periodo.');
  }
}

/**
 * Aggiorna i campi di un periodo. ratingMode e ratingPreset sono immutabili
 * se esiste almeno un match OFFICIAL nel periodo.
 * @param {string} periodId
 * @param {string} groupId
 * @param {Object} data - Campi da aggiornare
 * @returns {Promise<void>}
 */
export async function updatePeriod(periodId, groupId, data) {
  try {
    if (!periodId || !groupId) throw new Error('Periodo o gruppo mancante.');
    const payload = data && typeof data === 'object' ? { ...data } : {};
    if (payload.ratingMode !== undefined || payload.ratingPreset !== undefined) {
      const q = query(
        collection(db, COLLECTIONS.MATCHES),
        where(MATCHES_FIELDS.GROUP_ID, '==', groupId),
        where(MATCHES_FIELDS.PERIOD_ID, '==', periodId),
        where(MATCHES_FIELDS.RATING_IMPACT, '==', RATING_IMPACT.OFFICIAL),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        throw new Error(
          'Non è possibile modificare la modalità ELO dopo che esistono partite ufficiali nella stagione.'
        );
      }
    }
    const periodRef = doc(db, COLLECTIONS.PERIODS, periodId);
    const clean = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined)
    );
    await updateDoc(periodRef, {
      ...clean,
      [PERIODS_FIELDS.UPDATED_AT]: serverTimestamp(),
    });
  } catch (err) {
    throw new Error(err?.message || 'Errore nell\'aggiornamento del periodo.');
  }
}

/**
 * Recupera tutti i periodi del gruppo (ordinamento non garantito).
 * @param {string} groupId
 * @returns {Promise<Array<{ id: string, ...Object }>>}
 */
export async function getPeriodsByGroup(groupId) {
  try {
    if (!groupId) return [];
    const q = query(
      collection(db, COLLECTIONS.PERIODS),
      where(PERIODS_FIELDS.GROUP_ID, '==', groupId)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    throw new Error(err?.message || 'Errore nel caricamento dei periodi.');
  }
}
